import React from 'react';
import { blackScholesGreeks } from '../utils/blackScholes';
import { RISK_FREE_RATE } from '../utils/elasticityCalculations';
import { mobileColors } from '../theme/mobileTokens';

/**
 * Fixed-decimal Greek formatting, shared with the modal's fallback grid of
 * vendor-quoted Greeks so both render to the same precision.
 */
export const formatGreekNum = (v, decimals) => {
  if (v == null || isNaN(v)) return null;
  return Number(v).toFixed(decimals);
};

/**
 * GreeksTransitionSection — why this scenario cell moves, not just how much.
 *
 * Shows each Greek now and at the exit horizon, plus charm. Both columns are
 * MODELED from the same (S, K, T, r, sigma) the cell was priced with, which is
 * the point: pairing our projection against the vendor's quoted Greeks would
 * show a jump at the 0%/no-time-elapsed cell that is pure model disagreement
 * rather than price or time. The vendor's raw snapshot stays on the Greeks tab,
 * where a market quote belongs.
 *
 * Renders nothing unless the cell carries the pricing inputs (computeHeatmapRow
 * emits them), so a surface computing cells some other way keeps the old grid.
 */
function GreeksTransitionSection({ cell, ivTooltip }) {
  if (!cell || cell.timeToTarget == null || cell.ivNow == null) return null;

  const { strike, optionType, currentUnderlying, simulatedStock,
          timeNow, timeToTarget, ivNow, ivScenario, crushActive } = cell;

  const now = blackScholesGreeks(currentUnderlying, strike, timeNow, RISK_FREE_RATE, ivNow, optionType);
  const exit = blackScholesGreeks(simulatedStock, strike, timeToTarget, RISK_FREE_RATE, ivScenario, optionType);

  if (now.delta == null || exit.delta == null) return null;

  const rows = [
    { key: 'Delta', dp: 4, hint: 'premium per $1 of underlying' },
    { key: 'Gamma', dp: 4, hint: 'delta per $1 of underlying' },
    { key: 'Theta', dp: 4, hint: 'premium per day' },
    { key: 'Vega', dp: 4, hint: 'premium per IV point' },
  ];

  const ivNowPct = ivNow * 100;
  const ivExitPct = ivScenario * 100;
  const ivMoved = Math.abs(ivExitPct - ivNowPct) >= 0.005;

  const cellStyle = { fontSize: 13, color: mobileColors.textPrimary, fontVariantNumeric: 'tabular-nums' };
  const headStyle = { fontSize: 10, color: mobileColors.textTertiary, textTransform: 'uppercase', letterSpacing: 0.4 };

  return (
    <div style={{ padding: '12px 14px', borderBottom: `0.5px solid ${mobileColors.borderHair}` }}>
      <div style={{ ...headStyle, fontSize: 11, color: mobileColors.textSecondary, marginBottom: 2 }}>
        Greeks
      </div>
      <div style={{ fontSize: 11, color: mobileColors.textTertiary, marginBottom: 8 }}>
        Modeled at this scenario — now vs. at exit
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto', gap: '6px 10px', alignItems: 'center' }}>
        <span style={headStyle} />
        <span style={{ ...headStyle, textAlign: 'right' }}>Now</span>
        <span style={{ ...headStyle, textAlign: 'center' }} />
        <span style={{ ...headStyle, textAlign: 'right' }}>At exit</span>

        {rows.map(({ key, dp, hint }) => {
          const a = now[key.toLowerCase()];
          const b = exit[key.toLowerCase()];
          return (
            <React.Fragment key={key}>
              <span style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontSize: 12, color: mobileColors.textPrimary }}>{key}</span>
                <span style={{ fontSize: 10, color: mobileColors.textTertiary }}>{hint}</span>
              </span>
              <span style={{ ...cellStyle, textAlign: 'right' }}>{formatGreekNum(a, dp)}</span>
              <span style={{ fontSize: 12, color: mobileColors.textTertiary, textAlign: 'center' }}>→</span>
              <span style={{ ...cellStyle, textAlign: 'right', fontWeight: 600 }}>{formatGreekNum(b, dp)}</span>
            </React.Fragment>
          );
        })}

        {/* IV is not a Greek, but it is the input the sticky-moneyness and
            vol-crush toggles actually move — showing it here is what makes a
            loss at the 0% column legible instead of looking like a bug. */}
        <React.Fragment>
          <span style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontSize: 12, color: mobileColors.textPrimary }}>IV</span>
            <span style={{ fontSize: 10, color: mobileColors.textTertiary }}>
              {crushActive ? 'crushed past earnings' : ivMoved ? 'slid along the smile' : 'held flat'}
            </span>
          </span>
          <span
            className={ivTooltip ? 'iv-source-hint' : undefined}
            title={ivTooltip || undefined}
            style={{ ...cellStyle, textAlign: 'right' }}
          >
            {ivNowPct.toFixed(2)}%
          </span>
          <span style={{ fontSize: 12, color: mobileColors.textTertiary, textAlign: 'center' }}>→</span>
          <span style={{ ...cellStyle, textAlign: 'right', fontWeight: 600 }}>{ivExitPct.toFixed(2)}%</span>
        </React.Fragment>
      </div>

      {/* Charm is a rate, not a before/after pair — it is the reason delta moved
          between the two columns above, and the Greek that most explains
          short-dated behaviour (an ATM contract's delta running to 0 or 1
          through the afternoon). */}
      {now.charm != null && (
        <div style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 10,
          marginTop: 10,
          paddingTop: 10,
          borderTop: `0.5px solid ${mobileColors.borderHair}`,
        }}>
          <span style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontSize: 12, color: mobileColors.textPrimary }}>Charm</span>
            <span style={{ fontSize: 10, color: mobileColors.textTertiary }}>
              delta {now.charm < 0 ? 'lost' : 'gained'} per day held
            </span>
          </span>
          <span style={{ ...cellStyle, fontWeight: 600 }}>{formatGreekNum(now.charm, 4)}</span>
        </div>
      )}
    </div>
  );
}

export default GreeksTransitionSection;
